<template>
	<div class="widget-item" :class="{ active: activeForm?.name === element.name }">
		<el-form-item :label="getLabel(element)" :label-width="getLabelWidth(element)" :rules="element.options.rules">
			<template v-if="element.type === 'input'">
				<el-input
					v-model="value"
					:style="{ width: element.options.width }"
					:maxlength="element.options.maxlength"
					:placeholder="element.options.placeholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
					:readonly="element.options.readonly"
					:show-password="element.options.showPassword"
					:show-word-limit="element.options.showWordLimit"
				/>
			</template>

			<template v-if="element.type === 'textarea'">
				<el-input
					v-model="value"
					type="textarea"
					:style="{ width: element.options.width }"
					:maxlength="element.options.maxlength"
					:rows="element.options.rows"
					:placeholder="element.options.placeholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
					:readonly="element.options.readonly"
					:show-word-limit="element.options.showWordLimit"
				/>
			</template>

			<template v-if="element.type === 'number'">
				<el-input-number
					v-model="value"
					:style="{ width: element.options.width }"
					:max="element.options.max"
					:min="element.options.min"
					:step="element.options.step"
					:precision="element.options.precision"
					:controls-position="element.options.controlsPosition"
					:readonly="element.options.readonly"
					:disabled="element.options.disabled"
				/>
			</template>

			<template v-if="element.type === 'radio'">
				<el-radio-group v-model="value" :style="{ width: element.options.width }" :disabled="element.options.disabled">
					<template v-if="element.options.button">
						<el-radio-button v-for="(item, index) in element.options.options" :key="index" :value="item.value">
							{{ item.label }}
						</el-radio-button>
					</template>
					<template v-else>
						<el-radio v-for="(item, index) in element.options.options" :key="index" :value="item.value">
							{{ item.label }}
						</el-radio>
					</template>
				</el-radio-group>
			</template>

			<template v-if="element.type === 'checkbox'">
				<el-checkbox-group v-model="arrayData" :disabled="element.options.disabled">
					<template v-if="element.options.button">
						<el-checkbox-button v-for="item in element.options.options" :key="item.value" :value="item.value">{{ item.label }} </el-checkbox-button>
					</template>
					<template v-else>
						<el-checkbox v-for="item in element.options.options" :key="item.value" :value="item.value">{{ item.label }} </el-checkbox>
					</template>
				</el-checkbox-group>
			</template>

			<template v-if="element.type === 'select' && !element.options.multiple">
				<el-select
					v-model="value"
					:style="{ width: element.options.width }"
					:multiple="element.options.multiple"
					:placeholder="element.options.placeholder"
					:filterable="element.options.filterable"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				>
					<el-option v-for="item in element.options.options" :key="item.value" :value="item.value" :label="item.label" />
				</el-select>
			</template>

			<template v-if="element.type === 'select' && element.options.multiple">
				<el-select
					v-model="arrayData"
					:style="{ width: element.options.width }"
					:multiple="element.options.multiple"
					:placeholder="element.options.placeholder"
					:filterable="element.options.filterable"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				>
					<el-option v-for="item in element.options.options" :key="item.value" :value="item.value" :label="item.label" />
				</el-select>
			</template>

			<template v-if="element.type === 'time'">
				<el-time-picker
					v-if="element.options.isRange"
					v-model="value"
					:style="{ width: element.options.width }"
					:format="element.options.format"
					is-range
					:start-placeholder="element.options.startPlaceholder"
					:end-placeholder="element.options.endPlaceholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				/>
				<el-time-picker
					v-else
					v-model="value"
					:style="{ width: element.options.width }"
					:format="element.options.format"
					:placeholder="element.options.placeholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				/>
			</template>

			<template v-if="element.type === 'date'">
				<el-date-picker
					v-model="value"
					:style="{ width: element.options.width }"
					:type="element.options.type"
					:format="element.options.format"
					:value-format="element.options.format"
					:placeholder="element.options.placeholder"
					:start-placeholder="element.options.startPlaceholder"
					:end-placeholder="element.options.endPlaceholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				/>
			</template>

			<template v-if="element.type === 'rate'">
				<el-rate
					v-model="value"
					:style="{ width: element.options.width }"
					:max="element.options.max"
					:allow-half="element.options.allowHalf"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				/>
			</template>

			<template v-if="element.type === 'switch'">
				<el-switch v-model="value" :disabled="element.options.disabled" />
			</template>

			<template v-if="element.type === 'slider'">
				<el-slider
					v-model="value"
					:style="{ width: element.options.width }"
					:min="element.options.min"
					:max="element.options.max"
					:step="element.options.step"
					:show-input="element.options.showInput"
					:disabled="element.options.disabled"
				/>
			</template>

			<template v-if="element.type === 'text'">
				<el-text :type="element.options.type" :size="element.options.size">{{ element.options.defaultValue }}</el-text>
			</template>

			<template v-if="element.type === 'link'">
				<el-link :type="element.options.type" :href="element.options.href" target="_blank" :underline="element.options.underline">
					{{ element.options.defaultValue }}
				</el-link>
			</template>

			<template v-if="element.type === 'editor'">
				<ma-editor
					v-model="value"
					:placeholder="element.options.placeholder"
					:disabled="element.options.disabled"
					:height="element.options.height"
				></ma-editor>
			</template>

			<template v-if="element.type === 'file'">
				<el-upload disabled>
					<el-button>{{ $t('designForm.clickUpload') }}</el-button>
				</el-upload>
			</template>

			<template v-if="element.type === 'image'">
				<el-upload list-type="picture-card" disabled>
					<ma-icon icon="insert"></ma-icon>
				</el-upload>
			</template>

			<template v-if="element.type === 'cascader'">
				<el-cascader
					v-model="arrayData"
					:options="element.options.options"
					:style="{ width: element.options.width }"
					:props="{
						multiple: element.options.multiple,
						checkStrictly: element.options.checkStrictly
					}"
					:placeholder="element.options.placeholder"
					:filterable="element.options.filterable"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				/>
			</template>
			<template v-if="element.type === 'address'">
				<ma-address
					v-model="value"
					:style="{ width: element.options.width }"
					:placeholder="element.options.placeholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				>
				</ma-address>
			</template>

			<template v-if="element.type === 'treeselect'">
				<el-tree-select
					v-model="value"
					:props="{ label: element.options.props.label, value: element.options.props.value }"
					:data="element.options.options"
					:style="{ width: element.options.width }"
					:multiple="element.options.multiple"
					:placeholder="element.options.placeholder"
					:filterable="element.options.filterable"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
					:check-strictly="element.options.checkStrictly"
				/>
			</template>

			<template v-if="element.type === 'user'">
				<ma-user-input
					v-model="value"
					:style="{ width: element.options.width }"
					:placeholder="element.options.placeholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				>
				</ma-user-input>
			</template>

			<template v-if="element.type === 'org'">
				<ma-org-select
					v-model="value"
					:style="{ width: element.options.width }"
					:placeholder="element.options.placeholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				>
				</ma-org-select>
			</template>

			<template v-if="element.type === 'post'">
				<ma-post-input
					v-model="value"
					:style="{ width: element.options.width }"
					:placeholder="element.options.placeholder"
					:clearable="element.options.clearable"
					:disabled="element.options.disabled"
				>
				</ma-post-input>
			</template>

			<template v-if="element.type === 'divider'">
				<el-divider :content-position="element.options.position">{{ element.label }}</el-divider>
			</template>
		</el-form-item>

		<div class="drag-control">
			<div class="drag-move">
				<ma-icon icon="move"></ma-icon>
			</div>
			<div class="item-control">
				<ma-icon icon="clone" :title="$t('designForm.copy')" @click.stop="emits('copy')"></ma-icon>
				<ma-icon icon="delete" :title="$t('designForm.delete')" @click.stop="emits('delete')"></ma-icon>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'

const props = defineProps({
	element: {
		type: Object as any,
		required: true
	},
	modelValue: {
		type: [String, Number, Boolean, Date, Array],
		required: true
	},
	activeForm: {
		type: Object,
		default: null
	}
})

const value = ref(props.modelValue)

const getLabel = (item: any) => {
	return item.options.labelHide ? '' : item.label
}

const getLabelWidth = (item: any) => {
	return item.options.labelHide ? '0' : ''
}

watch(
	() => props.modelValue,
	() => {
		if (props.element.type === 'checkbox' || props.element.type === 'cascader' || props.element.options?.multiple) {
			if (!props.modelValue || Array.isArray(props.modelValue)) {
				arrayData.value = []
			} else {
				arrayData.value = (props.modelValue as string).split(',')
			}
		} else {
			value.value = props.modelValue
		}
	}
)

// 多选控件(如：复选框)
const arrayData = ref<any[]>([])

// 当多选控件值，发生变化时
watch(
	() => arrayData.value,
	newData => {
		if (!newData) {
			value.value = ''
		} else {
			value.value = newData.join(',')
		}
	}
)

const emits = defineEmits(['update:modelValue', 'copy', 'delete'])
watch(
	() => value.value,
	val => {
		emits('update:modelValue', val)
	}
)
</script>
